const LS_KEY = 'mdv-scene-data-panel';

export function readSceneDataPanelOpen() {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeSceneDataPanelOpen(open) {
  try {
    localStorage.setItem(LS_KEY, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function formatCount(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(Math.round(v));
}

export function formatMs(n) {
  const v = Number(n) || 0;
  return `${v.toFixed(v >= 20 ? 0 : 1)} ms`;
}

export function formatFps(n) {
  const v = Number(n) || 0;
  return v >= 20 ? String(Math.round(v)) : v.toFixed(1);
}

function geometryBytes(geo) {
  if (!geo?.attributes) return 0;
  let n = 0;
  const attrs = geo.attributes;
  for (const key in attrs) {
    const arr = attrs[key]?.array;
    if (arr?.byteLength) n += arr.byteLength;
  }
  if (geo.index?.array?.byteLength) n += geo.index.array.byteLength;
  return n;
}

function objectBytes(obj) {
  let n = geometryBytes(obj.geometry);
  const matrix = obj.instanceMatrix?.array;
  if (matrix?.byteLength) n += matrix.byteLength;
  const color = obj.instanceColor?.array;
  if (color?.byteLength) n += color.byteLength;
  return n;
}

function objectCount(obj) {
  if (obj.isInstancedMesh) return obj.count || 0;
  if (obj.isPoints) return obj.geometry?.attributes?.position?.count || 0;
  if (obj.isLine || obj.isLineSegments) return obj.geometry?.attributes?.position?.count || 0;
  if (obj.isMesh) {
    if (obj.geometry?.index) return Math.round(obj.geometry.index.count / 3);
    return Math.round((obj.geometry?.attributes?.position?.count || 0) / 3);
  }
  return 0;
}

function objectKind(obj) {
  if (obj.isInstancedMesh) return 'instanced';
  if (obj.isPoints) return 'points';
  if (obj.isLineSegments || obj.isLine) return 'lines';
  if (obj.isMesh) return 'mesh';
  return 'object';
}

function skipObject(obj) {
  if (!obj || obj.isCamera || obj.isLight || obj.isBone) return true;
  const name = obj.name || '';
  if (name.startsWith('SceneLook')) return true;
  if (obj.isHelper || obj.type?.endsWith('Helper')) return true;
  return false;
}

function layerKeyOf(obj) {
  let cur = obj;
  while (cur) {
    const key = cur.userData?.mdvLayerKey;
    if (key) return String(key);
    cur = cur.parent;
  }
  return obj.name || obj.type || 'unnamed';
}

function jsHeap() {
  const mem = typeof performance !== 'undefined' ? performance.memory : null;
  if (!mem || !mem.jsHeapSizeLimit) return null;
  return {
    used: mem.usedJSHeapSize || 0,
    total: mem.totalJSHeapSize || 0,
    limit: mem.jsHeapSizeLimit || 0,
  };
}

export function summariseLayers(layerVisibility = {}) {
  const rows = Object.entries(layerVisibility);
  let visible = 0;
  let loading = 0;
  let ready = 0;
  let failed = 0;
  let hiddenReady = 0;
  for (const [, state] of rows) {
    const status = state?.loadStatus || 'idle';
    const on = !!state?.visible;
    if (status === 'loading') loading += 1;
    if (status === 'failed') failed += 1;
    if (status === 'ready') {
      ready += 1;
      if (on) visible += 1;
      else hiddenReady += 1;
    } else if (on && status !== 'loading') {
      visible += 1;
    }
  }
  return {
    catalogue: rows.length,
    visible,
    loading,
    ready,
    failed,
    hiddenReady,
  };
}

export function sampleScene(three, layerVisibility) {
  const gl = three?.gl;
  const scene = three?.scene;
  const info = gl?.info;
  const render = info?.render || {};
  const memory = info?.memory || {};

  const layers = new Map();
  let meshes = 0;
  let points = 0;
  let instanced = 0;
  let lines = 0;
  let objects = 0;
  let visibleObjects = 0;
  let gpuBytes = 0;
  let vertices = 0;
  let instances = 0;
  let pointCount = 0;

  if (scene?.traverse) {
    scene.traverse((obj) => {
      objects += 1;
      if (obj.visible) visibleObjects += 1;
      if (skipObject(obj) || !obj.isObject3D) return;
      const kind = objectKind(obj);
      if (kind === 'object') return;
      const count = objectCount(obj);
      const bytes = objectBytes(obj);
      gpuBytes += bytes;
      if (kind === 'mesh') {
        meshes += 1;
        vertices += obj.geometry?.attributes?.position?.count || 0;
      } else if (kind === 'instanced') {
        instanced += 1;
        instances += count;
        vertices += obj.geometry?.attributes?.position?.count || 0;
      } else if (kind === 'points') {
        points += 1;
        pointCount += count;
      } else if (kind === 'lines') {
        lines += 1;
      }

      const key = layerKeyOf(obj);
      const row = layers.get(key) || {
        name: key,
        kind,
        count: 0,
        bytes: 0,
        visible: false,
        parts: 0,
      };
      row.count += count;
      row.bytes += bytes;
      row.parts += 1;
      row.visible = row.visible || (!!obj.visible && obj.parent?.visible !== false);
      if (kind === 'points' || kind === 'instanced') row.kind = kind;
      layers.set(key, row);
    });
  }

  const layerRows = Array.from(layers.values())
    .filter((row) => row.count > 0 || row.bytes > 0)
    .sort((a, b) => b.count - a.count || b.bytes - a.bytes)
    .slice(0, 48);

  return {
    calls: render.calls || 0,
    triangles: render.triangles || 0,
    renderPoints: render.points || 0,
    renderLines: render.lines || 0,
    geometries: memory.geometries || 0,
    textures: memory.textures || 0,
    programs: Array.isArray(info?.programs) ? info.programs.length : 0,
    meshes,
    points,
    instanced,
    lines,
    objects,
    visibleObjects,
    gpuBytes,
    vertices,
    instances,
    pointCount,
    layerRows,
    layers: summariseLayers(layerVisibility),
    heap: jsHeap(),
    deviceMemory: typeof navigator !== 'undefined' ? navigator.deviceMemory || null : null,
    ready: Boolean(gl && scene),
  };
}
